import React, { useState, useEffect } from "react";
import { authFetch, getToken, getUser } from "../pages/Auth";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { COLORS, RISK_COLORS, commonAxisStyle, commonGridStyle, tooltipStyle } from "../chartTheme";

const API_BASE = process.env.REACT_APP_API_URL || "https://pathriva-api-93474716090.us-central1.run.app";

const OBS_ICONS = {
  body_weight:       { emoji: "\u2696", color: "#00e5ff" },
  bmi:               { emoji: "\u2195", color: "#60a5fa" },
  medication_refill: { emoji: "\uD83D\uDC8A", color: "#34d399" },
  hemoglobin_a1c:    { emoji: "\uD83E\uDE78", color: "#f472b6" },
  ldl_cholesterol:   { emoji: "\u2665", color: "#fbbf24" },
  systolic_bp:       { emoji: "\uD83E\uDE7A", color: "#ff4d6d" },
  diastolic_bp:      { emoji: "\uD83E\uDE7A", color: "#fb923c" },
  default:           { emoji: "\u25CB", color: "#4a6175" },
};

export default function PatientDetail({ patient, onClose }) {
  const [detail, setDetail] = useState(null);
  const [observations, setObservations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [obsLoading, setObsLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [showTimeline, setShowTimeline] = useState(true);

  useEffect(() => { if(patient?.patient_id){loadDetail();loadObs();} }, [patient?.patient_id]); // eslint-disable-line

  async function loadDetail() {
    setLoading(true);setError("");
    try { const res=await authFetch(`/api/patients/${patient.patient_id}`);const data=await res.json();
      if(!res.ok){setError(data.error||"Failed");}else{setDetail(data.patient);} } catch{setError("Unable to connect");}
    setLoading(false);
  }
  async function loadObs() {
    setObsLoading(true);
    try { const res=await authFetch(`/api/patients/${patient.patient_id}/observations?limit=50`);const data=await res.json();
      if(res.ok)setObservations(data.observations); } catch{}
    setObsLoading(false);
  }
  async function downloadEvidence() {
    setDownloading(true);
    try {
      const res=await fetch(`${API_BASE}/api/evidence/packet/${patient.patient_id}`,{headers:{Authorization:`Bearer ${getToken()}`}});
      if(!res.ok){const d=await res.json().catch(()=>({}));alert(d.error||"Download failed");setDownloading(false);return;}
      const blob=await res.blob();const url=URL.createObjectURL(blob);
      const a=document.createElement("a");a.href=url;a.download=`pathriva_evidence_${patient.patient_id.slice(0,8)}.pdf`;
      document.body.appendChild(a);a.click();document.body.removeChild(a);URL.revokeObjectURL(url);
    } catch{alert("Download failed");}
    setDownloading(false);
  }

  const [riskData, setRiskData] = useState(null);
  const [paData, setPaData] = useState(null);
  const [paLoading, setPaLoading] = useState(false);
  const [paError, setPaError] = useState("");
  const [payerRules, setPayerRules] = useState(null);
  const [paExpanded, setPaExpanded] = useState(true);
  const [appealLetter, setAppealLetter] = useState(null);
  const [appealPayer, setAppealPayer] = useState(null);
  const [appealLoading, setAppealLoading] = useState(false);
  const [appealCopied, setAppealCopied] = useState(false);

  useEffect(() => {
    if (!patient?.patient_id) return;
    authFetch(`/api/ml/dropout-risk/${patient.patient_id}`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d && setRiskData(d))
      .catch(() => {});
    setPaLoading(true); setPaError("");
    authFetch(`/api/prior-auth/evaluate/${patient.patient_id}`)
      .then((r) => r.ok ? r.json() : Promise.reject("Failed"))
      .then((d) => { setPaData(d); setPaLoading(false); })
      .catch(() => { setPaError("Unable to load PA data"); setPaLoading(false); });
    authFetch(`/api/prior-auth/payers`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d && setPayerRules(d.payers))
      .catch(() => {});
  }, [patient?.patient_id]);

  const user = getUser();
  const canAppeal = user && ["super_admin", "admin", "operator"].includes(user.role);

  async function loadAppealLetter(payerId) {
    setAppealLoading(true); setAppealPayer(payerId); setAppealCopied(false);
    try {
      const res = await authFetch(`/api/prior-auth/appeal-letter/${patient.patient_id}?payer=${payerId}`);
      if (res.ok) { const d = await res.json(); setAppealLetter(d.letter); }
      else { setAppealLetter("Failed to generate appeal letter."); }
    } catch { setAppealLetter("Failed to generate appeal letter."); }
    setAppealLoading(false);
  }

  function copyAppealLetter() {
    if (!appealLetter) return;
    navigator.clipboard.writeText(appealLetter).then(() => {
      setAppealCopied(true);
      setTimeout(() => setAppealCopied(false), 2000);
    }).catch(() => {});
  }

  function getLikelihoodPct(likelihood) {
    if (likelihood === "likely_approved") return 87;
    if (likelihood === "conditional") return 62;
    return 28;
  }

  function getLikelihoodColor(pct) {
    if (pct > 80) return COLORS.success;
    if (pct >= 50) return COLORS.warning;
    return COLORS.danger;
  }

  const p=detail||patient;
  const comorbidities=[];
  if(p.has_type2_diabetes)comorbidities.push("Type 2 Diabetes");
  if(p.has_hypertension)comorbidities.push("Hypertension");
  if(p.has_hyperlipidemia)comorbidities.push("Hyperlipidemia");
  if(p.has_sleep_apnea)comorbidities.push("Sleep Apnea");
  if(p.has_cardiovascular)comorbidities.push("Cardiovascular");

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.panel} onClick={(e)=>e.stopPropagation()}>
        <div style={s.header}>
          <div>
            <div style={s.patientName}>{p.given_name?.split(" ")[0]} {p.family_name}</div>
            <div style={s.patientMeta}>{p.patient_id?.slice(0,12)}... &bull; {p.gender} &bull; {p.state||""}</div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={downloadEvidence} disabled={downloading} style={s.downloadBtn}>
              {downloading?"Generating...":"\u2193 Evidence Packet"}</button>
            <button onClick={onClose} style={s.closeBtn}>&times;</button>
          </div>
        </div>

        {loading && <div style={s.loadingBar}><div style={s.loadingFill}/></div>}
        {error && <div style={s.errorBanner}>{error}<button onClick={loadDetail} style={s.retryBtn}>Retry</button></div>}

        {!loading && !error && detail && (
          <>
            <div style={s.grid}>
              <IC l="DOB" v={fmtD(p.birth_date)}/><IC l="Enrollment" v={fmtD(p.enrollment_date)}/>
              <IC l="Status" v={p.is_active?"Active":"Inactive"} c={p.is_active?"#34d399":"#fbbf24"}/>
              <IC l="Program" v={p.program_name||"\u2014"}/>
              <IC l="BMI" v={p.bmi_at_enrollment||"\u2014"}/><IC l="Weight" v={p.weight_at_enrollment_lbs?`${p.weight_at_enrollment_lbs} lbs`:"\u2014"}/>
              <IC l="Height" v={p.height_inches?`${p.height_inches} in`:"\u2014"}/><IC l="Discharge" v={p.discharge_date?fmtD(p.discharge_date):"N/A"}/>
            </div>
            {comorbidities.length>0&&(
              <div style={s.section}><div style={s.secTitle}>Comorbidities</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {comorbidities.map(c=><span key={c} style={s.combBadge}>{c}</span>)}
                </div>
              </div>
            )}
            {p.discharge_reason&&<div style={s.section}><div style={s.secTitle}>Discharge Reason</div><div style={{fontSize:12,color:"#8b949e"}}>{p.discharge_reason}</div></div>}

            {/* Risk Score Card */}
            {riskData && (
              <div style={s.section}>
                <div style={s.secTitle}>Dropout Risk Assessment</div>
                <div style={{
                  padding: 16, borderRadius: 10, background: "#131920", border: "1px solid #1e2d3d",
                  display: "flex", alignItems: "center", gap: 16,
                }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: "50%",
                    background: `${RISK_COLORS[riskData.risk_category] || "#4a6175"}18`,
                    border: `2px solid ${RISK_COLORS[riskData.risk_category] || "#4a6175"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 18, fontWeight: 800, color: RISK_COLORS[riskData.risk_category] || "#4a6175",
                  }}>
                    {riskData.dropout_risk_score != null ? (riskData.dropout_risk_score * 100).toFixed(0) : "?"}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: RISK_COLORS[riskData.risk_category] || "#e6edf3", marginBottom: 4, textTransform: "uppercase" }}>
                      {riskData.risk_category || "Unknown"} Risk
                    </div>
                    {riskData.top_risk_factors && (
                      <div style={{ fontSize: 10, color: "#4a6175" }}>
                        Factors: {typeof riskData.top_risk_factors === "string" ? riskData.top_risk_factors : JSON.stringify(riskData.top_risk_factors)}
                      </div>
                    )}
                  </div>
                </div>
                {/* Intervention Recommendations */}
                {riskData.risk_category === "high" && (
                  <div style={{
                    marginTop: 10, padding: 12, borderRadius: 8,
                    background: "rgba(255,77,109,0.06)", border: "1px solid rgba(255,77,109,0.15)",
                  }}>
                    <div style={{ fontSize: 10, color: "#ff4d6d", fontWeight: 700, marginBottom: 6, letterSpacing: 1 }}>RECOMMENDED INTERVENTIONS</div>
                    <div style={{ fontSize: 11, color: "#8b949e", lineHeight: 1.6 }}>
                      &bull; Schedule outreach call within 48 hours<br/>
                      &bull; Verify medication refill status with pharmacy<br/>
                      &bull; Review side effect history and adjust if needed<br/>
                      &bull; Consider care coordinator follow-up visit
                    </div>
                  </div>
                )}
                {riskData.risk_category === "medium" && (
                  <div style={{
                    marginTop: 10, padding: 12, borderRadius: 8,
                    background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.15)",
                  }}>
                    <div style={{ fontSize: 10, color: "#fbbf24", fontWeight: 700, marginBottom: 6, letterSpacing: 1 }}>MONITORING RECOMMENDATIONS</div>
                    <div style={{ fontSize: 11, color: "#8b949e", lineHeight: 1.6 }}>
                      &bull; Monitor refill adherence over next 30 days<br/>
                      &bull; Send automated refill reminder<br/>
                      &bull; Flag for review at next care team meeting
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Prior Authorization Intelligence */}
            <div style={s.section}>
              <div
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", marginBottom: paExpanded ? 10 : 0 }}
                onClick={() => setPaExpanded(!paExpanded)}
              >
                <div style={s.secTitle}>Prior Authorization Intelligence</div>
                <span style={{ fontSize: 16, color: COLORS.muted, userSelect: "none" }}>{paExpanded ? "\u25B2" : "\u25BC"}</span>
              </div>
              {paExpanded && (
                <>
                  {paLoading && <div style={s.obsMsg}>Loading PA evaluation...</div>}
                  {paError && !paLoading && <div style={{ fontSize: 11, color: COLORS.danger, marginBottom: 8 }}>{paError}</div>}

                  {/* PA Likelihood Cards */}
                  {paData && paData.evaluations && (
                    <div style={{ marginBottom: 16 }}>
                      {Object.entries(paData.evaluations).map(([payerId, eval_]) => {
                        const pct = getLikelihoodPct(eval_.likelihood);
                        const color = getLikelihoodColor(pct);
                        const criteriaMet = eval_.issues?.filter(i => i === "All criteria met") || [];
                        const criteriaMissing = eval_.issues?.filter(i => i !== "All criteria met") || [];
                        return (
                          <div key={payerId} style={{
                            padding: 14, borderRadius: 10, marginBottom: 10,
                            background: COLORS.bg, border: `1px solid ${COLORS.border}`,
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.text }}>{eval_.payer_name}</span>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontSize: 18, fontWeight: 800, color }}>{pct}%</span>
                                <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 20, background: `${color}18`, color, fontWeight: 600, textTransform: "uppercase" }}>
                                  {pct > 80 ? "likely approved" : pct >= 50 ? "conditional" : "likely denied"}
                                </span>
                              </div>
                            </div>
                            {/* Progress bar */}
                            <div style={{ height: 4, background: COLORS.border, borderRadius: 2, marginBottom: 10 }}>
                              <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 2, transition: "width 0.5s ease" }} />
                            </div>
                            {/* Criteria */}
                            {criteriaMet.length > 0 && (
                              <div style={{ fontSize: 11, color: COLORS.success, marginBottom: 4 }}>
                                {"\u2713"} All criteria met
                              </div>
                            )}
                            {criteriaMissing.map((issue, i) => (
                              <div key={i} style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 2 }}>
                                {"\u2717"} {issue}
                              </div>
                            ))}
                            {/* Required docs */}
                            {eval_.step_therapy && eval_.step_therapy.length > 0 && (
                              <div style={{ marginTop: 6, fontSize: 10, color: COLORS.muted }}>
                                Step therapy: {eval_.step_therapy.join(", ").replace(/_/g, " ")}
                              </div>
                            )}
                            {eval_.max_duration_months && (
                              <div style={{ fontSize: 10, color: COLORS.muted }}>
                                Max approved duration: {eval_.max_duration_months} months
                              </div>
                            )}
                            {eval_.renewal_criteria && (
                              <div style={{ fontSize: 10, color: COLORS.muted }}>
                                Renewal: {eval_.renewal_criteria.replace(/_/g, " ")}
                              </div>
                            )}
                            {/* Appeal button */}
                            {canAppeal && (
                              <button
                                onClick={() => appealPayer === payerId && appealLetter ? (setAppealLetter(null), setAppealPayer(null)) : loadAppealLetter(payerId)}
                                disabled={appealLoading && appealPayer === payerId}
                                style={{
                                  marginTop: 10, background: "rgba(0,229,255,0.08)", border: `1px solid ${COLORS.primary}30`,
                                  borderRadius: 6, color: COLORS.primary, padding: "6px 14px", fontSize: 10,
                                  fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                                }}
                              >
                                {appealLoading && appealPayer === payerId ? "Generating..." : appealPayer === payerId && appealLetter ? "Hide Appeal Letter" : "Generate Appeal Letter"}
                              </button>
                            )}
                            {/* Appeal letter display */}
                            {appealPayer === payerId && appealLetter && !appealLoading && (
                              <div style={{
                                marginTop: 10, padding: 14, borderRadius: 8,
                                background: COLORS.bgDark, border: `1px solid ${COLORS.border}`,
                                maxHeight: 300, overflowY: "auto",
                              }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                                  <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.muted, letterSpacing: 1, textTransform: "uppercase" }}>Appeal Letter</span>
                                  <button
                                    onClick={copyAppealLetter}
                                    style={{
                                      background: appealCopied ? "rgba(52,211,153,0.15)" : "rgba(0,229,255,0.1)",
                                      border: `1px solid ${appealCopied ? COLORS.success : COLORS.primary}30`,
                                      borderRadius: 6, color: appealCopied ? COLORS.success : COLORS.primary,
                                      padding: "4px 10px", fontSize: 10, cursor: "pointer", fontFamily: "inherit", fontWeight: 600,
                                    }}
                                  >
                                    {appealCopied ? "Copied!" : "Copy to Clipboard"}
                                  </button>
                                </div>
                                <pre style={{
                                  fontSize: 11, color: COLORS.textDim, lineHeight: 1.6,
                                  whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0,
                                  fontFamily: "'JetBrains Mono', monospace",
                                }}>{appealLetter}</pre>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Payer Rules Summary Table */}
                  {payerRules && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.muted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Payer Formulary Rules</div>
                      <div style={{ overflowX: "auto" }}>
                        <table style={s.table}>
                          <thead>
                            <tr>
                              <th style={s.th}>Payer</th>
                              <th style={s.th}>BMI Req</th>
                              <th style={s.th}>PA Required</th>
                              <th style={s.th}>Covered Medications</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(payerRules).map(([id, rule]) => (
                              <tr key={id}>
                                <td style={{ ...s.td, fontWeight: 600, color: COLORS.text }}>{rule.name}</td>
                                <td style={s.td}>{"\u2265"} {rule.requires_bmi}</td>
                                <td style={s.td}>{rule.prior_auth_required ? "Yes" : "No"}</td>
                                <td style={{ ...s.td, fontSize: 10 }}>{rule.covered_medications?.join(", ")}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Weight Chart */}
            {observations && observations.filter(o => o.observation_type === "body_weight").length > 1 && (
              <div style={s.section}>
                <div style={s.secTitle}>Weight Trajectory</div>
                <div style={{ background: "#131920", border: "1px solid #1e2d3d", borderRadius: 10, padding: 16 }}>
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={observations
                      .filter(o => o.observation_type === "body_weight" && o.value_quantity != null)
                      .sort((a, b) => (a.observation_date || "").localeCompare(b.observation_date || ""))
                      .map(o => ({
                        date: (o.observation_date || "").slice(0, 7),
                        weight: Number(o.value_quantity),
                      }))
                    }>
                      <CartesianGrid {...commonGridStyle} />
                      <XAxis dataKey="date" tick={commonAxisStyle} axisLine={false} tickLine={false} />
                      <YAxis tick={commonAxisStyle} axisLine={false} tickLine={false} domain={["auto", "auto"]} />
                      <Tooltip {...tooltipStyle} formatter={(v) => [`${v} lbs`, "Weight"]} />
                      <Line type="monotone" dataKey="weight" stroke={COLORS.primary} strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </>
        )}

        <div style={s.section}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={s.secTitle}>Clinical Observations{observations&&<span style={{fontWeight:400,color:"#4a6175"}}> ({observations.length})</span>}</div>
            {observations && observations.length > 0 && (
              <button onClick={() => setShowTimeline(!showTimeline)} style={{
                background: "#131920", border: "1px solid #1e2d3d", borderRadius: 6,
                color: "#4a6175", padding: "4px 10px", fontSize: 10, cursor: "pointer",
              }}>{showTimeline ? "Table View" : "Timeline View"}</button>
            )}
          </div>
          {obsLoading?<div style={s.obsMsg}>Loading observations...</div>
           :!observations||observations.length===0?<div style={s.obsMsg}>No observations recorded</div>
           : showTimeline ? (
            <div style={{ maxHeight: 400, overflowY: "auto", paddingLeft: 8 }}>
              {observations.map((o, i) => {
                const icon = OBS_ICONS[o.observation_type] || OBS_ICONS.default;
                return (
                  <div key={o.observation_id || i} style={{ display: "flex", gap: 12, position: "relative" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 32 }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: "50%", display: "flex",
                        alignItems: "center", justifyContent: "center", fontSize: 14,
                        background: `${icon.color}15`, border: `1px solid ${icon.color}40`, flexShrink: 0,
                      }}>{icon.emoji}</div>
                      {i < observations.length - 1 && (
                        <div style={{ width: 1, flex: 1, minHeight: 16, background: "#1e2d3d" }} />
                      )}
                    </div>
                    <div style={{ paddingBottom: 12, flex: 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#e6edf3" }}>
                          {o.observation_type?.replace(/_/g, " ") || "Observation"}
                        </span>
                        <span style={{ fontSize: 10, color: "#4a6175", fontFamily: "monospace" }}>
                          {fmtD(o.observation_date)}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: icon.color, marginTop: 2 }}>
                        {o.value_quantity ?? "\u2014"} {o.value_unit || ""}
                      </div>
                      {o.loinc_display && <div style={{ fontSize: 9, color: "#4a6175", marginTop: 2 }}>{o.loinc_display}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
           ) : (
            <div style={{overflowX:"auto",maxHeight:300,overflowY:"auto"}}>
              <table style={s.table}>
                <thead><tr><th style={s.th}>Date</th><th style={s.th}>Type</th><th style={s.th}>Value</th><th style={s.th}>Unit</th><th style={s.th}>LOINC</th><th style={s.th}>Source</th></tr></thead>
                <tbody>{observations.map((o,i)=>(
                  <tr key={o.observation_id||i}>
                    <td style={s.td}>{fmtD(o.observation_date)}</td><td style={s.td}>{o.observation_type}</td>
                    <td style={{...s.td,fontWeight:600,color:"#e6edf3"}}>{o.value_quantity??"\u2014"}</td>
                    <td style={s.td}>{o.value_unit||"\u2014"}</td>
                    <td style={{...s.td,fontFamily:"'JetBrains Mono',monospace",fontSize:10}}>{o.loinc_code||"\u2014"}</td>
                    <td style={s.td}>{o.data_source||"\u2014"}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function IC({l,v,c}){return(<div style={s.ic}><div style={s.icL}>{l}</div><div style={{fontSize:13,fontWeight:600,color:c||"#e6edf3"}}>{v}</div></div>);}
function fmtD(d){if(!d)return"\u2014";try{return new Date(d).toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"});}catch{return d;}}

const s = {
  overlay:{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:1000,display:"flex",justifyContent:"flex-end"},
  panel:{width:"100%",maxWidth:680,background:"#0d1117",borderLeft:"1px solid #1e2d3d",height:"100%",overflowY:"auto",padding:24},
  header:{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,paddingBottom:16,borderBottom:"1px solid #1e2d3d"},
  patientName:{fontSize:18,fontWeight:700,color:"#e6edf3",marginBottom:4},
  patientMeta:{fontSize:11,color:"#4a6175",fontFamily:"'JetBrains Mono',monospace"},
  downloadBtn:{background:"linear-gradient(135deg,#00e5ff,#0099cc)",border:"none",borderRadius:8,color:"#000",padding:"8px 16px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"},
  closeBtn:{background:"#131920",border:"1px solid #1e2d3d",borderRadius:8,color:"#8b949e",padding:"8px 12px",fontSize:13,cursor:"pointer"},
  loadingBar:{height:3,background:"#1e2d3d",borderRadius:2,overflow:"hidden",marginBottom:16},
  loadingFill:{height:"100%",width:"60%",background:"linear-gradient(90deg,#00e5ff,#7c3aed)",borderRadius:2},
  errorBanner:{display:"flex",alignItems:"center",justifyContent:"space-between",background:"rgba(255,77,109,0.08)",border:"1px solid rgba(255,77,109,0.25)",borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:12,color:"#ff4d6d"},
  retryBtn:{background:"rgba(255,77,109,0.15)",border:"1px solid rgba(255,77,109,0.3)",borderRadius:6,color:"#ff4d6d",padding:"4px 10px",fontSize:10,cursor:"pointer",fontFamily:"inherit"},
  grid:{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:20},
  ic:{background:"#131920",border:"1px solid #1e2d3d",borderRadius:8,padding:12},
  icL:{fontSize:9,color:"#4a6175",letterSpacing:1,textTransform:"uppercase",marginBottom:4},
  section:{marginBottom:20},
  secTitle:{fontSize:12,fontWeight:700,color:"#e6edf3",marginBottom:10,letterSpacing:0.5},
  combBadge:{padding:"3px 10px",borderRadius:20,fontSize:10,fontWeight:600,background:"rgba(244,114,182,0.12)",color:"#f472b6",border:"1px solid rgba(244,114,182,0.25)"},
  table:{width:"100%",borderCollapse:"collapse"},
  th:{textAlign:"left",padding:"6px 8px",fontSize:9,fontWeight:600,color:"#4a6175",letterSpacing:1,textTransform:"uppercase",borderBottom:"1px solid #1e2d3d",position:"sticky",top:0,background:"#0d1117"},
  td:{padding:"7px 8px",fontSize:11,color:"#8b949e",borderBottom:"1px solid rgba(255,255,255,0.04)"},
  obsMsg:{color:"#4a6175",fontSize:11,padding:20,textAlign:"center"},
};
